import {animate, style, transition, trigger} from '@angular/animations';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {ActivatedRoute} from '@angular/router';
import {Store} from '@ngrx/store';
import * as moment from 'moment-timezone';
import {filter, map, switchMap} from 'rxjs';
import {CSChallengeCycle, CSChallengeCycleMetric, CurrentSkedChallenge} from 'src/app/core/model/project';
import {GetCSChallengesCycleAction} from 'src/app/core/store/current-sked/current-sked.action';
import {selectChallengeById, selectChallengeCyclesById} from 'src/app/core/store/current-sked/current-sked.selector';
import {MessageService} from 'src/app/services/message.service';
import {TimezoneService} from 'src/app/services/timezone.service';
import {getColorForName} from 'src/app/shared/utils/name-to-color.util';
import {TrackerMetricTypes} from 'src/app/trackers/shared/tracker-metric-types';
import {normalizeMetricValues} from '../shared/cycle-metric.util';
import {MatDialog} from '@angular/material/dialog';
import {OphLoadingModule} from 'src/app/shared/design/oph-loading/oph-loading.module';
import {OphMetricBarChartComponent} from 'src/app/shared/design/oph-metric-bar-chart/oph-metric-bar-chart.component';
import {CsChallengeDayCycleSelectorComponent} from '../shared/cs-challenge-day-cycle-selector/cs-challenge-day-cycle-selector.component';
import {
  CsCycleChartDetailDialogComponent,
  CsCycleChartDetailDialogData,
} from './chart-detail/cs-cycle-chart-detail-dialog.component';
import {MobileService} from 'src/app/core/page/mobile.service';

const CONTENT_ANIMATION_DURATION = '220ms';

export interface CycleStubLevel {
  name: string;
  streak: number;
  progress: boolean[];
  currentIndex: number;
}

export interface CycleStubItem {
  metricId: string;
  name: string;
  icon: string;
  color: string;
  score: number;
  displayTotal?: number;
  dailyScores: number[];
}

@Component({
  selector: 'cs-challenges-cycles',
  standalone: true,
  imports: [CsChallengeDayCycleSelectorComponent, OphLoadingModule, OphMetricBarChartComponent],
  templateUrl: './cs-challenges-cycles.component.html',
  styleUrl: './cs-challenges-cycles.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('slideContent', [
      transition(':decrement', [
        style({transform: 'translateX(-50px)', opacity: 0}),
        animate(
          `${CONTENT_ANIMATION_DURATION} cubic-bezier(0.25, 0.46, 0.45, 0.94)`,
          style({transform: 'translateX(0)', opacity: 1})
        ),
      ]),
      transition(':increment', [
        style({transform: 'translateX(50px)', opacity: 0}),
        animate(
          `${CONTENT_ANIMATION_DURATION} cubic-bezier(0.25, 0.46, 0.45, 0.94)`,
          style({transform: 'translateX(0)', opacity: 1})
        ),
      ]),
    ]),
  ],
})
export class CsChallengesCyclesComponent implements OnInit {
  private readonly mobileService = inject(MobileService);
  private readonly store = inject(Store);
  private readonly dialog = inject(MatDialog);
  private readonly timezoneService = inject(TimezoneService);
  private readonly messageService = inject(MessageService);
  private readonly activatedRoute = inject(ActivatedRoute);

  /** If the parent has a persisted cycle key, pass it in so we resume there. */
  readonly initialCycleKey = input<string | null>(null);
  readonly selectedCycleKeyChange = output<string>();

  /** True while fetching cycle data from the store (request in flight and no data yet). */
  public readonly loading = signal(false);

  /** True while fetching a previous cycle. Shown as a spinner on the prev chevron. */
  public readonly loadingPrev = signal(false);

  private readonly challengeCycles = toSignal(
    this.activatedRoute.paramMap.pipe(
      map(params => params.get('id')),
      filter((id): id is string => !!id),
      switchMap(id => this.store.select(selectChallengeCyclesById(id)))
    ),
    {initialValue: null as Record<string, CSChallengeCycle> | null}
  );

  private readonly challenge = toSignal(
    this.activatedRoute.paramMap.pipe(
      map(params => params.get('id')),
      filter((id): id is string => !!id),
      switchMap(id => this.store.select(selectChallengeById(id)))
    ),
    {initialValue: null as CurrentSkedChallenge | null}
  );

  public readonly todayMoment = signal<moment.Moment | null>(null);
  private readonly selectedCycleKey = signal<string | null>(null);
  private readonly projectId = signal<string | null>(null);
  private readonly todayDateString = signal<string | null>(null);
  private readonly cycleDisplayOverride = signal<string | null>(null);
  private readonly latestCycleNumber = signal<number | null>(null);
  public readonly animationCounter = signal(0);

  private readonly projectCycles = toSignal(
    this.activatedRoute.paramMap.pipe(
      map(params => params.get('id')),
      filter((id): id is string => !!id),
      switchMap(id => this.store.select(selectChallengeCyclesById(id)))
    ),
    {initialValue: null as Record<string, CSChallengeCycle> | null}
  );

  public readonly fetchedCycle = computed(() => {
    const cycles = this.projectCycles();
    if (!cycles) return null;
    const today = this.todayMoment();
    if (!today) return null;

    const tz = this.timezoneService.getTimeZone();
    const cyclesList = Object.values(cycles);
    return (
      cyclesList.find(cycle => {
        const start = moment.tz(cycle.cycleStartDate, tz).startOf('day');
        const end = moment.tz(cycle.cycleEndDate, tz).endOf('day');
        return today.isBetween(start, end, 'day', '[]');
      }) ??
      cyclesList[0] ??
      null
    );
  });

  public readonly selectedCycle = computed(() => {
    const cycles = this.challengeCycles();
    if (!cycles) return null;

    const key = this.selectedCycleKey();
    if (key) return cycles[key] ?? null;

    // Default: find today's cycle by date range (only before first fetch sets the key)
    const tz = this.timezoneService.getTimeZone();
    const today = moment().tz(tz).startOf('day');
    return (
      Object.values(cycles).find(cycle => {
        const start = moment.tz(cycle.cycleStartDate, tz).startOf('day');
        const end = moment.tz(cycle.cycleEndDate, tz).endOf('day');
        return today.isBetween(start, end, 'day', '[]');
      }) ?? null
    );
  });

  readonly cycleDisplayText = computed(() => {
    const override = this.cycleDisplayOverride();
    if (override !== null) return override;

    const cycle = this.selectedCycle();
    if (!cycle) return '';
    const tz = this.challenge().timezone || this.timezoneService.getTimeZone();
    const start = moment.tz(cycle.cycleStartDate, tz).format('MMM D');
    const end = moment.tz(cycle.cycleEndDate, tz).subtract(1, 'second').format('MMM D');
    return `Cycle ${cycle.cycleNumber}, ${start} - ${end}`;
  });

  readonly isPrevCycleDisabled = computed(() => {
    const current = this.selectedCycle();
    if (!current) return true;
    return current.cycleNumber <= 1;
  });

  readonly isNextCycleDisabled = computed(() => {
    const current = this.selectedCycle();
    if (!current) return true;
    const latest = this.latestCycleNumber();
    if (latest === null) return true;
    return current.cycleNumber >= latest;
  });

  readonly cycleItems = computed(() => {
    const cycle = this.selectedCycle() ?? this.fetchedCycle();
    if (!cycle?.cycleMetrics) return [];

    return cycle.cycleMetrics.map(
      (metric): CycleStubItem => ({
        metricId: metric.metricId,
        name: metric.metricName,
        icon: metric.taskIcon,
        color: getColorForName(metric.metricId, 'metric'),
        score: Math.round(metric.percentComplete),
        displayTotal: this.getDisplayTotal(metric),
        dailyScores: normalizeMetricValues(metric.metricValues, metric.targetValue, metric.metricPeriod),
      })
    );
  });

  private getDisplayTotal(metric: CSChallengeCycleMetric): number | undefined {
    if (metric.metricType === TrackerMetricTypes.Boolean) {
      return undefined;
    }
    return metric.targetValue;
  }

  readonly stubLevel: CycleStubLevel = {
    name: 'Level 1',
    streak: 3,
    progress: [true, false, false, true, true, true, false, false, false, false],
    currentIndex: 6,
  };

  readonly circumference = 2 * Math.PI * 10;

  constructor() {
    effect(() => {
      const key = this.selectedCycleKey();
      if (key) this.selectedCycleKeyChange.emit(key);
    });
  }

  /** Stroke-dashoffset for the meter circle; clamps score to 100 so over-100% shows fully filled. */
  getMeterDashOffset(score: number): number {
    const ratio = Math.min(score, 100) / 100;
    return this.circumference * (1 - ratio);
  }

  /**
   * Fetches cycle data for the given project and date. Manages loading state and error messaging.
   * @param onSuccess - Optional callback with the fetched cycle (e.g. to set selected cycle id).
   */
  private fetchCycle(projectId: string, date: string, onSuccess?: (cycle: CSChallengeCycle) => void): void {
    this.loading.set(true);
    this.store.dispatch(
      new GetCSChallengesCycleAction({
        projectId,
        date,
        onSuccess: cycle => {
          this.loading.set(false);
          onSuccess?.(cycle);
        },
        onFailure: err => {
          this.loading.set(false);
          this.messageService.add(err?.message ?? 'Failed to load cycle', 'error');
        },
      })
    );
  }

  public ngOnInit(): void {
    const tz = this.timezoneService.getTimeZone();
    const today = moment().tz(tz).startOf('day');
    this.todayMoment.set(today);

    const todayStr = today.format('YYYY-MM-DD');
    this.todayDateString.set(todayStr);

    const initialKey = this.initialCycleKey();

    const id = this.activatedRoute.snapshot.paramMap.get('id');
    if (id) {
      this.projectId.set(id);

      // Always fetch today's cycle to establish latestCycleNumber
      this.fetchCycle(id, todayStr, todayCycle => {
        this.latestCycleNumber.set(todayCycle.cycleNumber);

        if (!initialKey) {
          this.selectedCycleKey.set(todayCycle.cycleStartDate);
        }
      });

      if (initialKey) {
        // Restore persisted cycle — set key immediately, fetch its data separately
        this.selectedCycleKey.set(initialKey);
        if (initialKey !== todayStr) {
          this.store.dispatch(
            new GetCSChallengesCycleAction({
              projectId: id,
              date: initialKey,
              onSuccess: () => {},
              onFailure: err => this.messageService.add(err?.message ?? 'Failed to load cycle', 'error'),
            })
          );
        }
      }
    }
  }

  public onPrevCycle(): void {
    const current = this.selectedCycle();
    if (!current || this.loadingPrev()) return;

    const challenge = this.challenge();
    const cycleLength = challenge?.cycleLength ?? 7;
    const projectId = this.projectId();
    if (!projectId) return;

    const prevCycleMoment = moment
      .tz(current.cycleStartDate, current.challengeTimezone)
      .subtract(cycleLength, 'days')
      .startOf('day');
    const prevCycleDate = prevCycleMoment.format('YYYY-MM-DD');

    // Update display text and animation immediately
    const prevNumber = current.cycleNumber - 1;
    const prevStart = prevCycleMoment.format('MMM D');
    const prevEnd = prevCycleMoment
      .clone()
      .add(cycleLength - 1, 'days')
      .format('MMM D');
    this.cycleDisplayOverride.set(`Cycle ${prevNumber}, ${prevStart} - ${prevEnd}`);
    this.animationCounter.update(v => v - 1);

    // Show cached cycle immediately if already in the store
    const cycles = this.challengeCycles();
    const cached = cycles?.[prevCycleDate];

    if (cached) {
      this.selectedCycleKey.set(prevCycleDate);
    } else {
      this.loadingPrev.set(true);
    }

    // Always fetch fresh data
    this.store.dispatch(
      new GetCSChallengesCycleAction({
        projectId,
        date: prevCycleDate,
        onSuccess: cycle => {
          this.selectedCycleKey.set(cycle.cycleStartDate);
          this.loadingPrev.set(false);
          this.cycleDisplayOverride.set(null);
        },
        onFailure: err => {
          this.loadingPrev.set(false);
          this.cycleDisplayOverride.set(null);
          this.messageService.add(err?.message ?? 'Failed to load cycle', 'error');
        },
      })
    );
  }

  public onNextCycle(): void {
    const current = this.selectedCycle();
    if (!current) return;
    const cycles = this.challengeCycles();
    if (!cycles) return;

    const nextNumber = current.cycleNumber + 1;
    const nextCycle = Object.values(cycles).find(c => c.cycleNumber === nextNumber);
    if (nextCycle) {
      this.selectedCycleKey.set(nextCycle.cycleStartDate);
      this.animationCounter.update(v => v + 1);
    }
  }

  onItemClick(item: CycleStubItem): void {
    const cycle = this.selectedCycle() ?? this.fetchedCycle();
    if (!cycle) return;
    const metric = cycle.cycleMetrics?.find(m => m.metricId === item.metricId);
    if (!metric) return;
    const dialogData: CsCycleChartDetailDialogData = {cycle, metric};
    this.dialog.open(CsCycleChartDetailDialogComponent, {
      autoFocus: this.mobileService.isMobilePlatform() ? false : undefined,
      data: dialogData,
      panelClass: 'cs-cycle-chart-detail-dialog',
      width: '100%',
      maxWidth: '400px',
    });
  }
}
